import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, Check, AlertCircle, Sparkles } from 'lucide-react';

export default function DocumentScanner({ user }) {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('auto');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [scannedDocs, setScannedDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScannedDocs();
  }, []);

  const loadScannedDocs = () => {
    api.get('/documents/scanned')
      .then(res => setScannedDocs(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Please upload a JPEG, PNG, WEBP, or PDF file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleScan = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setScanning(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    try {
      const res = await api.post('/documents/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      toast.success('Document scanned successfully!');
      loadScannedDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan document');
    } finally {
      setScanning(false);
    }
  };

  const renderExtractedData = (data) => {
    if (!data) return null;
    
    return (
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => {
          if (value === null || value === undefined) return null;
          
          // Skip raw_response as it's too long
          if (key === 'raw_response') return null;
          
          // Format the key nicely
          const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          // Handle nested objects
          if (typeof value === 'object' && !Array.isArray(value)) {
            return (
              <div key={key} className="border-l-2 border-blue-200 pl-3">
                <p className="font-medium text-slate-700">{formattedKey}:</p>
                <div className="ml-2 text-sm">
                  {Object.entries(value).map(([k, v]) => (
                    <p key={k}><span className="text-slate-500">{k}:</span> {String(v)}</p>
                  ))}
                </div>
              </div>
            );
          }
          
          // Handle arrays
          if (Array.isArray(value)) {
            return (
              <div key={key}>
                <p className="font-medium text-slate-700">{formattedKey}:</p>
                <ul className="list-disc list-inside text-sm ml-2">
                  {value.map((item, i) => (
                    <li key={i}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
                  ))}
                </ul>
              </div>
            );
          }
          
          return (
            <p key={key} className="text-sm">
              <span className="font-medium text-slate-700">{formattedKey}:</span>{' '}
              <span className="text-slate-600">{String(value)}</span>
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">AI Document Scanner</h1>
        <p className="text-slate-600 mt-1">Upload rate confirmations, invoices, or load tenders to extract data automatically</p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Scan New Document
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-Detect</SelectItem>
                  <SelectItem value="rate_confirmation">Rate Confirmation</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="load_tender">Load Tender</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Upload Document</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleFileChange}
                data-testid="doc-file-input"
              />
              <p className="text-xs text-slate-500">Supported: JPEG, PNG, WEBP, PDF</p>
            </div>
          </div>

          {file && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">{file.name}</span>
              <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}

          <Button 
            onClick={handleScan} 
            disabled={!file || scanning}
            className="w-full"
            data-testid="scan-btn"
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Scanning with AI...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Scan Document
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Check className="w-5 h-5" />
              Extracted Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-slate-500 mb-3">
                Document Type: <span className="font-medium text-slate-700">{result.document_type}</span>
              </p>
              {renderExtractedData(result.extracted_data)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previous Scans */}
      <Card>
        <CardHeader>
          <CardTitle>Previously Scanned Documents</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : scannedDocs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No documents scanned yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scannedDocs.slice(0, 10).map(doc => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                  onClick={() => setResult({ document_type: doc.document_type, extracted_data: doc.extracted_data })}
                  data-testid={`scanned-doc-${doc.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.original_filename}</p>
                      <p className="text-xs text-slate-500">
                        {doc.document_type} • {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">View</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
